import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import { clearToken } from '@/utils/auth';
import { ssoLogout } from '@/api/sso';
import { getUserWorkspacesApi } from '@/api/user';
import {
  clearAnalyticsUser,
  syncAnalyticsUser,
  trackEvent,
} from '@/utils/analytics';
import { UserState } from './types';

// localStorage 键名（用于持久化和新标签页回退）
const WORKSPACE_LOCAL_STORAGE_KEY = 'user_workspace_default';

// 从 URL 中提取 workspace_uuid
const getWorkspaceFromUrl = (): string | undefined => {
  try {
    const path = window.location.pathname;
    const match = path.match(/^\/space\/([^/]+)/);
    return match ? match[1] : undefined;
  } catch (e) {
    console.error('Failed to extract workspace from URL:', e);
    return undefined;
  }
};

// 从 localStorage 读取默认项目信息（用于回退）
const loadWorkspaceFromLocal = () => {
  try {
    const localStored = localStorage.getItem(WORKSPACE_LOCAL_STORAGE_KEY);
    if (localStored) {
      const parsed = JSON.parse(localStored);
      if (parsed.tenantUuid && parsed.workspaceUuid) {
        return {
          tenantUuid: parsed.tenantUuid,
          workspaceUuid: parsed.workspaceUuid,
        };
      }
    }
  } catch (e) {
    console.error('Failed to load workspace from localStorage:', e);
  }

  return { tenantUuid: undefined, workspaceUuid: undefined };
};

// 保存项目信息到 localStorage（持久化，作为新标签页的默认值）
const saveWorkspaceToLocal = (tenantUuid?: string, workspaceUuid?: string) => {
  try {
    if (tenantUuid && workspaceUuid) {
      localStorage.setItem(
        WORKSPACE_LOCAL_STORAGE_KEY,
        JSON.stringify({ tenantUuid, workspaceUuid })
      );
    }
  } catch (e) {
    console.error('Failed to save workspace to localStorage:', e);
  }
};

const useUserStore = defineStore('user', {
  state: (): UserState => {
    // 是否激活过应用中心
    const hasUsedInviteCode = 2;

    // 优先从 URL 中读取 workspace_uuid
    const urlWorkspaceUuid = getWorkspaceFromUrl();

    // 如果 URL 中有 workspace_uuid，使用 URL 中的
    // 否则从 localStorage 读取（用于回退）
    let workspaceUuid = urlWorkspaceUuid;
    let tenantUuid: string | undefined;

    if (!workspaceUuid) {
      const workspace = loadWorkspaceFromLocal();
      workspaceUuid = workspace.workspaceUuid;
      tenantUuid = workspace.tenantUuid;
    }
    // 注意：如果是从 URL 读取的 workspaceUuid，tenantUuid 需要在后续通过 workspaceList 查找

    return {
      user_uuid: '',
      email: '',
      nickname: '',
      full_name: '',
      avatar_url: '',
      state: 1,
      created_at: '',
      updated_at: '',
      workspaceList: [],
      tenantUuid,
      workspaceUuid,
      hasUsedInviteCode,
    };
  },

  getters: {
    userInfo(state: UserState): UserState {
      return {
        user_uuid: state.user_uuid,
        email: state.email,
        nickname: state.nickname,
        full_name: state.full_name,
        state: state.state,
        created_at: state.created_at,
        updated_at: state.updated_at,
      };
    },
    // 添加 projectId getter 以兼容现有代码
    // projectId 实际上是 workspace_uuid，但为了兼容，我们返回一个标识符
    projectId(): string | undefined {
      return this.workspaceUuid;
    },
  },

  persist: {
    key: 'user',
    storage: localStorage,
    pick: [
      // 'user_uuid',
      'email',
      'nickname',
      'full_name',
      'avatar_url',
      // tenantUuid 和 workspaceUuid 不再使用 localStorage，改用 sessionStorage
    ],
  },

  actions: {
    // Set user's information
    setInfo(partial: Partial<UserState>) {
      const previousUserUuid = this.user_uuid;
      this.$patch(partial);

      // 同步用户信息到 Sentry
      Sentry.setUser({
        id: this.user_uuid,
        email: this.email,
        username: this.full_name || this.nickname,
      });

      // 如果更新了 tenantUuid 或 workspaceUuid，同步到 localStorage
      if (
        partial.tenantUuid !== undefined ||
        partial.workspaceUuid !== undefined
      ) {
        saveWorkspaceToLocal(
          partial.tenantUuid ?? this.tenantUuid,
          partial.workspaceUuid ?? this.workspaceUuid
        );
      }

      syncAnalyticsUser(this.user_uuid, {
        tenant_uuid: this.tenantUuid,
        workspace_uuid: this.workspaceUuid,
        ...this.userInfo,
      });

      if (!previousUserUuid && this.user_uuid) {
        trackEvent('login_success', {});
      }
    },

    changeHasInviteCode(status: 1 | 2) {
      this.hasUsedInviteCode = status;
    },

    // 设置项目信息（专门用于项目切换）
    // 保存到 localStorage（持久化，作为新标签页的默认值）
    // 注意：实际的 workspace_uuid 会从 URL 中读取，这里只是更新 store 状态
    setWorkspace(tenantUuid: string, workspaceUuid: string) {
      const previousWorkspaceUuid = this.workspaceUuid;
      this.tenantUuid = tenantUuid;
      this.workspaceUuid = workspaceUuid;

      // 设置 Sentry 标签，方便按空间搜索错误
      Sentry.setTag('tenant_uuid', tenantUuid);
      Sentry.setTag('workspace_uuid', workspaceUuid);

      // 保存到 localStorage（持久化，作为新标签页的默认值）
      saveWorkspaceToLocal(tenantUuid, workspaceUuid);

      syncAnalyticsUser(this.user_uuid, {
        tenant_uuid: tenantUuid,
        workspace_uuid: workspaceUuid,
        ...this.userInfo,
      });

      if (previousWorkspaceUuid && previousWorkspaceUuid !== workspaceUuid) {
        trackEvent('workspace_switch', {});
      }
    },

    // Reset user's information
    resetInfo() {
      this.$reset();
      // 清除 Sentry 用户信息
      Sentry.setUser(null);
      clearAnalyticsUser();
      // 清除存储中的项目信息
      localStorage.removeItem(WORKSPACE_LOCAL_STORAGE_KEY);
    },

    async fetchUserWorkspaceList() {
      const res: any = await getUserWorkspacesApi();
      this.workspaceList = res.data.results || [];

      // 如果当前有 workspaceUuid（从 URL 或 localStorage），尝试找到对应的 tenant_uuid
      if (this.workspaceUuid && this.workspaceList.length) {
        const currentWorkspace = this.workspaceList.find(
          (project: any) => project.workspace_uuid === this.workspaceUuid
        );

        if (currentWorkspace) {
          // 找到了对应的 workspace，更新 tenant_uuid
          this.setWorkspace(
            currentWorkspace.tenant_uuid,
            currentWorkspace.workspace_uuid
          );
        } else if (this.workspaceList.length > 0) {
          // 当前 workspaceUuid 无效，使用第一个
          const firstWorkspace = this.workspaceList[0] as any;
          this.setWorkspace(
            firstWorkspace.tenant_uuid,
            firstWorkspace.workspace_uuid
          );
        }
      } else if (this.workspaceList.length > 0) {
        // 没有 workspaceUuid，使用第一个
        const firstWorkspace = this.workspaceList[0] as any;
        this.setWorkspace(
          firstWorkspace.tenant_uuid,
          firstWorkspace.workspace_uuid
        );
      }
    },

    async logout() {
      trackEvent('logout', {});
      this.resetInfo();
      await ssoLogout();
      clearToken();
    },
  },
});

export default useUserStore;
